import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyOnBoardingToken, setOnBoardingPassword } from '../../auth/onBoarding.service';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
}));

const SetupContainer = styled(Stack)(({ theme }) => ({
  height: '100dvh',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "url('https://airsmart.com/airsmart-cms/wp-content/uploads/2018/04/INFORM_LucasSt11575_R.jpg')",
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
}));

const SetupPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  // Lấy email từ kết quả verify token nếu backend trả về
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid setup link.');
      setVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const result = await verifyOnBoardingToken(token);
        if (result.valid) {
          setTokenValid(true);
        } else {
          setError('This setup link has expired or is invalid.');
        }
      } catch (err) {
        setError('Failed to verify link. Please try again.');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (pwd) => {
    if (pwd.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordError(validatePassword(newPassword));

    if (confirmPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    if (password !== newConfirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    const passwordValidationError = validatePassword(password);

    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      // Tạm thời dùng resetPassword do flow tương tự
      await setOnBoardingPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to set up password.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (verifying) {
    return (
      <SetupContainer direction="column" justifyContent="center" alignItems="center">
        <StyledCard>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              Verifying Setup Link...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we verify your setup link.
            </Typography>
          </CardContent>
        </StyledCard>
      </SetupContainer>
    );
  }

  if (!tokenValid && !success) {
    return (
      <SetupContainer direction="column" justifyContent="center" alignItems="center">
        <StyledCard>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <ErrorIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="error">
              Invalid Setup Link
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error || 'This setup link is invalid or has expired.'}
            </Typography>
          </CardContent>
        </StyledCard>
      </SetupContainer>
    );
  }

  if (success) {
    return (
      <SetupContainer direction="column" justifyContent="center" alignItems="center">
        <StyledCard>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="success.main">
              Password Setup Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You can sign in and start onboarding now.
            </Typography>
            <Button variant="contained" onClick={handleGoToLogin} fullWidth size="small">
              Sign In Now
            </Button>
          </CardContent>
        </StyledCard>
      </SetupContainer>
    );
  }

  return (
    <SetupContainer direction="column" justifyContent="center" alignItems="center">
      <StyledCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <img src="/customshine.png" alt="Customshine Logo" width={30} height={30} />
            <span style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>Welcome to Customshine Onboarding</span>
          </Box>


          {/* Thêm hướng dẫn rõ ràng để người dùng biết cần nhập mật khẩu */}
          <Alert severity="info" sx={{ mb: 2 }}>
            To set your password, please enter a new password and confirm it below.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              placeholder="New Password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              error={!!passwordError}
              helperText={passwordError || 'Minimum 6 characters required'}
              margin="normal"
              required
              autoFocus
              size="small"
              InputLabelProps={{
                
                sx: {
                  color: 'text.primary',
                  '&.Mui-focused': { color: 'text.primary' },
                },
              }}
              InputProps={{
                sx: {
                  bgcolor: 'background.paper',
                },
              }}
              
            />
            
            <TextField
              fullWidth
              placeholder="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError || 'Re-enter your new password'}
              margin="normal"
              required
              size="small"
              InputLabelProps={{
                sx: {
                  color: 'text.primary',
                  '&.Mui-focused': { color: 'text.primary' },
                },
              }}
              InputProps={{
                sx: {
                  bgcolor: 'background.paper',
                },
              }}
             
            />

         
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 1 }}
              startIcon={loading ? <CircularProgress size={16} /> : null}
              size="small"
            >
              {loading ? 'Setting Password...' : 'Set Password'}
            </Button>
          </Box>
        </CardContent>
      </StyledCard>
    </SetupContainer>
  );
};

export default SetupPasswordPage;